class UpsellCheckboxBundle extends HTMLElement {
  constructor() {
    super();

    this.boundOnClick = this.onClick.bind(this);
    this.boundOnChange = this.onChange.bind(this);
    this.boundOnSubmit = this.onSubmit.bind(this);
    this.boundOnPriceMutation = this.onPriceMutation.bind(this);

    this.cards = [];
    this.mainPriceObserver = null;
    this.mainPriceFrame = null;
  }

  connectedCallback() {
    this.mainFormId = this.dataset.mainFormId || "";
    this.mainForm = this.mainFormId ? document.getElementById(this.mainFormId) : null;
    this.cards = Array.from(this.querySelectorAll("[data-upsell-card]")).slice(0, 2);
    this.updateMainPriceEnabled = this.dataset.updateMainPrice === "true";
    this.mainPriceRoot =
      this.closest("product-rerender") ||
      this.closest(".shopify-section") ||
      document;

    if (!this.cards.length || !this.mainForm) return;

    // The submit handler is attached only to this product form, so mark it as upsell-aware.
    this.mainForm.dataset.upsellAware = "true";

    this.setupCards();
    this.syncAllCardsUi();

    this.addEventListener("click", this.boundOnClick);
    this.addEventListener("change", this.boundOnChange);
    this.mainForm.addEventListener("submit", this.boundOnSubmit, true);

    this.observeMainPrice();
    this.scheduleMainPriceUpdate();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.boundOnClick);
    this.removeEventListener("change", this.boundOnChange);

    if (this.mainForm) {
      this.mainForm.removeEventListener("submit", this.boundOnSubmit, true);
    }

    if (this.mainPriceObserver) {
      this.mainPriceObserver.disconnect();
      this.mainPriceObserver = null;
    }

    if (this.mainPriceFrame) {
      cancelAnimationFrame(this.mainPriceFrame);
      this.mainPriceFrame = null;
    }
  }

  parseCents(value, fallback = 0) {
    const parsed = Number.parseInt(String(value || ""), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  formatMoney(cents) {
    const currencyCodeEnabled = this.dataset.currencyCodeEnabled === "true";
    const format = currencyCodeEnabled
      ? this.dataset.moneyWithCurrencyFormat
      : this.dataset.moneyFormat;

    if (format && /{{\s*\w+\s*}}/.test(format)) {
      const match = format.match(/{{\s*(\w+)\s*}}/);
      const placeholder = match?.[1] || "amount";
      const amount = this.formatMoneyAmount(cents, placeholder);
      const rendered = format.replace(match[0], amount);
      const template = document.createElement("template");
      template.innerHTML = rendered;
      return (template.content.textContent || rendered).trim();
    }

    const locale = this.dataset.locale || document.documentElement.lang || "en";
    const currency = this.dataset.currencyCode || "USD";

    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        currencyDisplay: currencyCodeEnabled ? "code" : "symbol"
      }).format(cents / 100);
    } catch (_error) {
      return (cents / 100).toFixed(2);
    }
  }

  formatMoneyAmount(cents, placeholder) {
    const value = cents / 100;
    const noDecimals = placeholder.includes("no_decimals");
    const decimals = noDecimals ? 0 : 2;

    let decimalSeparator = ".";
    let thousandsSeparator = ",";

    if (placeholder.includes("comma_separator")) {
      decimalSeparator = ",";
      thousandsSeparator = ".";
    } else if (placeholder.includes("apostrophe_separator")) {
      thousandsSeparator = "'";
    } else if (placeholder.includes("space_separator")) {
      thousandsSeparator = " ";
      decimalSeparator = placeholder.includes("with_comma") ? "," : ".";
    }

    const fixed = value.toFixed(decimals);
    const [integerPart, decimalPart] = fixed.split(".");
    const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

    return decimals ? `${groupedInteger}${decimalSeparator}${decimalPart}` : groupedInteger;
  }

  getVariantPricing(option) {
    if (!option) return null;

    const priceCents = this.parseCents(option.dataset.priceCents);
    const comparePriceCents = this.parseCents(
      option.dataset.comparePriceCents,
      priceCents
    );

    return {
      priceCents,
      comparePriceCents: Math.max(comparePriceCents, priceCents)
    };
  }

  setPriceDisplayFromOption(container, option) {
    const pricing = this.getVariantPricing(option);

    if (!pricing) {
      this.setPriceDisplay(container, "Select options", "");
      return;
    }

    const compareText =
      pricing.comparePriceCents > pricing.priceCents
        ? this.formatMoney(pricing.comparePriceCents)
        : "";

    this.setPriceDisplay(
      container,
      this.formatMoney(pricing.priceCents),
      compareText
    );
  }

  setPriceDisplay(container, currentText, compareText = "") {
    if (!container) return;

    const currentEl = container.querySelector("[data-upsell-price-current]");
    const compareEl = container.querySelector("[data-upsell-price-compare]");

    if (currentEl) {
      currentEl.textContent = currentText || "";
    } else {
      container.textContent = currentText || "";
    }

    if (compareEl) {
      if (compareText) {
        compareEl.textContent = compareText;
        compareEl.hidden = false;
      } else {
        compareEl.textContent = "";
        compareEl.hidden = true;
      }
    }
  }

  setupCards() {
    this.cards.forEach((card) => {
      const state = { selected: ["", "", ""] };
      card.__upsellState = state;

      const singleVariant = card.querySelector("[data-upsell-single-variant]");
      const variantIdInput = card.querySelector("[data-upsell-variant-id]");
      const priceEl = card.querySelector("[data-upsell-price]");
      const map = card.querySelector("[data-upsell-variant-map]");

      const firstAvailableOption = Array.from(map?.options || []).find((option) => {
        return option.value && option.dataset.available === "true";
      });

      if (singleVariant && variantIdInput) {
        variantIdInput.value = singleVariant.value;
        if (map) map.value = singleVariant.value;

        const match = map?.querySelector(
          `option[value="${CSS.escape(singleVariant.value)}"]`
        );

        if (priceEl && match) {
          this.setPriceDisplayFromOption(priceEl, match);
        }
      } else if (firstAvailableOption && variantIdInput) {
        variantIdInput.value = firstAvailableOption.value;
        if (map) map.value = firstAvailableOption.value;

        if (priceEl) {
          this.setPriceDisplayFromOption(priceEl, firstAvailableOption);
        }

        state.selected = [
          firstAvailableOption.dataset.option1 || "",
          firstAvailableOption.dataset.option2 || "",
          firstAvailableOption.dataset.option3 || ""
        ];
      }

      this.updateCardUi(card);
    });
  }

  isCardSelected(card) {
    return !!card.querySelector("[data-upsell-product-checkbox]")?.checked;
  }

  syncAllCardsUi() {
    this.cards.forEach((card) => {
      card.classList.toggle("is-selected", this.isCardSelected(card));
      this.updateCardUi(card);
    });

    this.scheduleMainPriceUpdate();
  }

  onChange(event) {
    const checkbox = event.target.closest("[data-upsell-product-checkbox]");
    // if (!checkbox || !this.contains(checkbox)) return;

    // Handle checkbox changes separately, so the dropdown change event can continue.
    if (checkbox && this.contains(checkbox)) {
      this.syncAllCardsUi();
      return;
    }
    const select = event.target.closest("[data-upsell-option-select]");
    if (!select || !this.contains(select)) return;

    select.dataset.optionValue = select.value;
    this.onClick({ target: select });
  }

  onClick(event) {
    const pill = event.target.closest("[data-option-value]");
    if (!pill || !this.contains(pill)) return;
    if (pill.disabled) return;

    const card = pill.closest("[data-upsell-card]");
    if (!card || !this.isCardSelected(card)) return;

    const group = pill.closest("[data-option-index]");
    if (!group) return;

    const idx = Number(group.dataset.optionIndex);
    const value = pill.dataset.optionValue || "";
    const state = card.__upsellState;

    if (state.selected[idx] === value) return;

    state.selected[idx] = value;
    this.autofillMissingOptions(card);
    this.refreshCardFromSelection(card);
  }

  autofillMissingOptions(card) {
    const state = card.__upsellState;
    const map = card.querySelector("[data-upsell-variant-map]");
    if (!map) return;

    const options = Array.from(map.options).filter((option) => {
      if (!option.value) return false;
      return this.matchesPartialSelection(option, state.selected);
    });

    const candidate =
      options.find((option) => option.dataset.available === "true") || options[0];

    if (!candidate) return;

    if (!state.selected[0] && candidate.dataset.option1) {
      state.selected[0] = candidate.dataset.option1;
    }
    if (!state.selected[1] && candidate.dataset.option2) {
      state.selected[1] = candidate.dataset.option2;
    }
    if (!state.selected[2] && candidate.dataset.option3) {
      state.selected[2] = candidate.dataset.option3;
    }
  }

  matchesPartialSelection(option, selected) {
    return [1, 2, 3].every((n, index) => {
      const selectedValue = selected[index];
      if (!selectedValue) return true;
      return (option.dataset[`option${n}`] || "") === selectedValue;
    });
  }

  hasCompleteSelection(card) {
    const groups = card.querySelectorAll("[data-option-index]");
    const state = card.__upsellState;

    return Array.from(groups).every((group) => {
      const idx = Number(group.dataset.optionIndex);
      return !!state.selected[idx];
    });
  }

  refreshCardFromSelection(card) {
    const state = card.__upsellState;
    const map = card.querySelector("[data-upsell-variant-map]");
    const variantIdInput = card.querySelector("[data-upsell-variant-id]");
    const priceEl = card.querySelector("[data-upsell-price]");
    const helpEl = card.querySelector("[data-upsell-help]");
    const options = Array.from(map?.options || []).filter((option) => option.value);

    const exact = options.find((option) => {
      return [1, 2, 3].every((n, index) => {
        const current = state.selected[index];
        if (!current) return true;
        return (option.dataset[`option${n}`] || "") === current;
      });
    });

    if (
      exact &&
      exact.value &&
      exact.dataset.available === "true" &&
      this.hasCompleteSelection(card)
    ) {
      if (variantIdInput) variantIdInput.value = exact.value;
      if (map) map.value = exact.value;
      if (priceEl) this.setPriceDisplayFromOption(priceEl, exact);
      if (helpEl) helpEl.textContent = "";
    } else {
      if (variantIdInput) variantIdInput.value = "";
      if (map) map.value = "";
      if (priceEl) this.setPriceDisplay(priceEl, "Select options", "");

      if (helpEl) {
        helpEl.textContent = this.hasCompleteSelection(card)
          ? "This combination is unavailable."
          : "Please select all required options.";
      }
    }

    this.updateCardUi(card);
    this.scheduleMainPriceUpdate();
  }

  updateCardUi(card) {
    const state = card.__upsellState;
    const groups = card.querySelectorAll("[data-option-index]");
    const map = card.querySelector("[data-upsell-variant-map]");
    const options = Array.from(map?.options || []).filter((option) => option.value);

    groups.forEach((group) => {
      const idx = Number(group.dataset.optionIndex);
      const selectedLabel = group.querySelector("[data-selected-label]");
      // const pills = Array.from(group.querySelectorAll("[data-option-value]"));
        const pills = Array.from(
        group.querySelectorAll(".upsell-card__pill[data-option-value]")
      );

      if (selectedLabel) selectedLabel.textContent = state.selected[idx] || "";

      pills.forEach((pill) => {
        const value = pill.dataset.optionValue || "";
        const active = state.selected[idx] === value;

        pill.setAttribute("aria-pressed", active ? "true" : "false");
        pill.classList.toggle("is-active", active);

        const possible = options.some((option) => {
          const probe = [...state.selected];
          probe[idx] = value;
          return (
            this.matchesPartialSelection(option, probe) &&
            option.dataset.available === "true"
          );
        });

        pill.disabled = !possible;
      });

      // it is showing selected value in dropdown option
      const select = group.querySelector("[data-upsell-option-select]");

      if (select) {
        select.value = state.selected[idx] || "";
      }
    });
  }

  getSelectedVariantOption(card) {
    const map = card.querySelector("[data-upsell-variant-map]");
    const variantId = String(
      card.querySelector("[data-upsell-variant-id]")?.value || ""
    ).trim();

    if (!map || !variantId) return null;

    return map.querySelector(`option[value="${CSS.escape(variantId)}"]`);
  }

  getSelectedUpsellTotals() {
    return this.cards.reduce(
      (totals, card) => {
        if (!this.isCardSelected(card)) return totals;

        const option = this.getSelectedVariantOption(card);
        if (!option || option.dataset.available !== "true") return totals;

        const pricing = this.getVariantPricing(option);
        if (!pricing) return totals;

        totals.priceCents += pricing.priceCents;
        totals.comparePriceCents += pricing.comparePriceCents;
        return totals;
      },
      { priceCents: 0, comparePriceCents: 0 }
    );
  }

  observeMainPrice() {
    if (!this.updateMainPriceEnabled || !this.mainPriceRoot) return;

    this.mainPriceObserver = new MutationObserver(this.boundOnPriceMutation);
    this.mainPriceObserver.observe(this.mainPriceRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        "data-base-price-cents",
        "data-base-compare-price-cents"
      ]
    });
  }

  onPriceMutation(mutations) {
    const relevantMutation = mutations.some((mutation) => {
      if (mutation.type === "attributes") return true;

      return Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element)) return false;
        return (
          node.matches?.("[data-main-product-price]") ||
          node.querySelector?.("[data-main-product-price]")
        );
      });
    });

    // Text updates made by this component can also trigger child-list mutations.
    // Scheduling is harmless because updateMainPrice skips unchanged values.
    if (relevantMutation || mutations.some((mutation) => mutation.type === "childList")) {
      this.scheduleMainPriceUpdate();
    }
  }

  scheduleMainPriceUpdate() {
    if (!this.updateMainPriceEnabled || this.mainPriceFrame) return;

    this.mainPriceFrame = requestAnimationFrame(() => {
      this.mainPriceFrame = null;
      this.updateMainPrice();
    });
  }

  getMainPriceContainer() {
    return this.mainPriceRoot?.querySelector?.("[data-main-product-price]") || null;
  }

  getElementPriceText(element) {
    if (!element) return "";

    return Array.from(element.childNodes)
      .filter((node) => {
        return !(
          node.nodeType === Node.ELEMENT_NODE &&
          node.matches?.(".sr-only")
        );
      })
      .map((node) => node.textContent || "")
      .join("")
      .trim();
  }

  setAccessiblePriceText(element, text, accessibleLabel) {
    if (!element) return;

    const currentText = this.getElementPriceText(element);
    if (currentText === text && element.dataset.upsellPriceValue === text) return;

    let srOnly = element.querySelector(".sr-only");

    if (!srOnly && accessibleLabel) {
      srOnly = document.createElement("span");
      srOnly.className = "sr-only";
      srOnly.textContent = accessibleLabel;
    }

    element.replaceChildren();
    if (srOnly) element.append(srOnly);
    element.append(document.createTextNode(text));
    element.dataset.upsellPriceValue = text;
  }

  ensureSalePrice(priceList) {
    let salePrice = priceList.querySelector("sale-price");
    if (salePrice) return salePrice;

    salePrice = document.createElement("sale-price");
    salePrice.className = "h6";
    priceList.prepend(salePrice);
    return salePrice;
  }

  ensureCompareAtPrice(priceList, salePrice) {
    let compareAtPrice = priceList.querySelector("compare-at-price");
    if (compareAtPrice) return compareAtPrice;

    compareAtPrice = document.createElement("compare-at-price");
    compareAtPrice.className = "h7 line-through";

    const badgeList = priceList.querySelector("badge-list");
    if (badgeList) {
      priceList.insertBefore(compareAtPrice, badgeList);
    } else {
      salePrice.after(compareAtPrice);
    }

    return compareAtPrice;
  }

  updateSaleBadge(priceList, currentCents, compareCents) {
    const badge = priceList.querySelector("on-sale-badge");
    if (!badge) return;

    const isOnSale = compareCents > currentCents;
    badge.hidden = !isOnSale;

    if (!isOnSale) return;

    const percentage = Math.round(
      ((compareCents - currentCents) / compareCents) * 100
    );

    const currentText = badge.textContent.trim();
    const nextText = /\d+(?:\.\d+)?%/.test(currentText)
      ? currentText.replace(/\d+(?:\.\d+)?%/, `${percentage}%`)
      : `Save ${percentage}%`;

    if (currentText !== nextText) badge.textContent = nextText;
  }

  updateMainPrice() {
    const container = this.getMainPriceContainer();
    if (!container) return;

    const basePriceCents = this.parseCents(container.dataset.basePriceCents);
    const baseComparePriceCents = this.parseCents(
      container.dataset.baseComparePriceCents,
      basePriceCents
    );
    const upsellTotals = this.getSelectedUpsellTotals();

    const totalPriceCents = basePriceCents + upsellTotals.priceCents;
    const totalComparePriceCents =
      Math.max(baseComparePriceCents, basePriceCents) +
      upsellTotals.comparePriceCents;
    const showComparePrice = totalComparePriceCents > totalPriceCents;

    const priceList = container.querySelector("price-list");
    if (!priceList) return;

    const salePrice = this.ensureSalePrice(priceList);
    const salePriceLabel =
      salePrice.querySelector(".sr-only")?.textContent?.trim() || "Sale price";

    this.setAccessiblePriceText(
      salePrice,
      this.formatMoney(totalPriceCents),
      salePriceLabel
    );
    salePrice.classList.toggle("text-on-sale", showComparePrice);

    let compareAtPrice = priceList.querySelector("compare-at-price");

    if (showComparePrice) {
      compareAtPrice = this.ensureCompareAtPrice(priceList, salePrice);
      const compareLabel =
        compareAtPrice.querySelector(".sr-only")?.textContent?.trim() ||
        "Regular price";

      this.setAccessiblePriceText(
        compareAtPrice,
        this.formatMoney(totalComparePriceCents),
        compareLabel
      );
      compareAtPrice.hidden = false;
    } else if (compareAtPrice) {
      compareAtPrice.hidden = true;
    }

    this.updateSaleBadge(priceList, totalPriceCents, totalComparePriceCents);
  }

  getEligibleUpsellItems() {
    return this.cards
      .filter((card) => this.isCardSelected(card))
      .map((card) => {
        const variantId = String(
          card.querySelector("[data-upsell-variant-id]")?.value || ""
        ).trim();

        if (!variantId) return null;

        return {
          id: Number(variantId),
          quantity: 1
        };
      })
      .filter(Boolean);
  }

  async onSubmit(event) {
    const form = event.target;
    if (form !== this.mainForm) return;
    if (!form.matches("[data-upsell-aware='true']")) return;

    const idInput = form.querySelector('input[name="id"]');
    if (!idInput?.value) return;

    const upsellItems = this.getEligibleUpsellItems();
    if (!upsellItems.length) return;

    event.preventDefault();
    event.stopPropagation();

    const quantityInput = form.querySelector('input[name="quantity"]');
    const mainQty = quantityInput ? Number.parseInt(quantityInput.value, 10) || 1 : 1;
    const mainVariantId = Number(idInput.value);

    const submitButtons = Array.from(form.querySelectorAll('[type="submit"]'));
    submitButtons.forEach((button) => button.setAttribute("aria-busy", "true"));

    document.documentElement.dispatchEvent(
      new CustomEvent("theme:loading:start", { bubbles: true })
    );

    try {
      await this.addBundleWithThemeEvents(
        form,
        { id: mainVariantId, quantity: mainQty },
        upsellItems
      );
    } finally {
      submitButtons.forEach((button) => button.removeAttribute("aria-busy"));
      document.documentElement.dispatchEvent(
        new CustomEvent("theme:loading:end", { bubbles: true })
      );
    }
  }

  async addBundleWithThemeEvents(mainForm, mainItem, upsellItems) {
    const sectionsToBundle = new Set();

    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    const payload = {
      items: [mainItem, ...upsellItems],
      sections: Array.from(sectionsToBundle).join(",")
    };

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify(payload)
    });

    const responseJson = await response.json();

    if (!response.ok) {
      mainForm.dispatchEvent(
        new CustomEvent("cart:error", {
          bubbles: true,
          detail: { error: responseJson.message }
        })
      );

      document.dispatchEvent(
        new CustomEvent("toast:show", {
          detail: {
            message: responseJson.message,
            tone: "error"
          }
        })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", { bubbles: true })
      );

      return;
    }

    if (
      window.themeVariables?.settings?.cartType === "page" ||
      window.themeVariables?.settings?.pageType === "cart"
    ) {
      window.location.href = `${Shopify.routes.root}cart`;
      return;
    }

    const cartContent = await (
      await fetch(`${Shopify.routes.root}cart.js`)
    ).json();
    cartContent.sections = responseJson.sections;

    const items = Object.prototype.hasOwnProperty.call(responseJson, "items")
      ? responseJson.items
      : [responseJson];

    mainForm.dispatchEvent(
      new CustomEvent("variant:add", {
        bubbles: true,
        detail: { items, cart: cartContent }
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
          cart: cartContent
        }
      })
    );

    if (window.themeVariables?.settings?.cartType === "message") {
      document.dispatchEvent(
        new CustomEvent("toast:show", {
          detail: {
            message: window.themeVariables.strings.addedToCart.replace(
              "{{product_title}}",
              items?.[0]?.title || ""
            )
          }
        })
      );
    }
  }
}

if (!window.customElements.get("upsell-checkbox-bundle")) {
  window.customElements.define("upsell-checkbox-bundle", UpsellCheckboxBundle);
}













// class UpsellCheckboxBundle extends HTMLElement {
//   constructor() {
//     super();
//     this.boundOnClick = this.onClick.bind(this);
//     this.boundOnChange = this.onChange.bind(this);
//     this.boundOnSubmit = this.onSubmit.bind(this);
//     this.cards = [];
//   }

//   connectedCallback() {
//     this.mainFormId = this.dataset.mainFormId || "";
//     this.mainForm = this.mainFormId ? document.getElementById(this.mainFormId) : null;
//     this.cards = Array.from(this.querySelectorAll("[data-upsell-card]")).slice(0, 2);

//     if (!this.cards.length || !this.mainForm) return;

//     this.setupCards();
// this.syncAllCardsUi();

//     this.addEventListener("click", this.boundOnClick);
//     this.addEventListener("change", this.boundOnChange);
//     this.mainForm.addEventListener("submit", this.boundOnSubmit, true);
//   }

//   disconnectedCallback() {
//     this.removeEventListener("click", this.boundOnClick);
//     this.removeEventListener("change", this.boundOnChange);

//     if (this.mainForm) {
//       this.mainForm.removeEventListener("submit", this.boundOnSubmit, true);
//     }
//   }

//   setPriceDisplay(container, currentText, compareText = "") {
//     if (!container) return;

//     const currentEl = container.querySelector("[data-upsell-price-current]");
//     const compareEl = container.querySelector("[data-upsell-price-compare]");

//     if (currentEl) {
//       currentEl.textContent = currentText || "";
//     } else {
//       container.textContent = currentText || "";
//     }

//     if (compareEl) {
//       if (compareText) {
//         compareEl.textContent = compareText;
//         compareEl.hidden = false;
//       } else {
//         compareEl.textContent = "";
//         compareEl.hidden = true;
//       }
//     }
//   }

//   setupCards() {
//     this.cards.forEach((card) => {
//       const state = { selected: ["", "", ""] };
//       card.__upsellState = state;

//       const singleVariant = card.querySelector("[data-upsell-single-variant]");
//       const variantIdInput = card.querySelector("[data-upsell-variant-id]");
//       const priceEl = card.querySelector("[data-upsell-price]");
//       const map = card.querySelector("[data-upsell-variant-map]");

//       const firstAvailableOption = Array.from(map?.options || []).find((option) => {
//         return option.value && option.dataset.available === "true";
//       });

//       if (singleVariant && variantIdInput) {
//         variantIdInput.value = singleVariant.value;
//         if (map) map.value = singleVariant.value;

//         const match = map?.querySelector(`option[value="${CSS.escape(singleVariant.value)}"]`);
//         if (priceEl && match) {
//           this.setPriceDisplay(
//             priceEl,
//             match.dataset.price || "",
//             match.dataset.comparePrice || ""
//           );
//         }
//       } else if (firstAvailableOption && variantIdInput) {
//         variantIdInput.value = firstAvailableOption.value;
//         if (map) map.value = firstAvailableOption.value;

//         if (priceEl) {
//           this.setPriceDisplay(
//             priceEl,
//             firstAvailableOption.dataset.price || "",
//             firstAvailableOption.dataset.comparePrice || ""
//           );
//         }

//         state.selected = [
//           firstAvailableOption.dataset.option1 || "",
//           firstAvailableOption.dataset.option2 || "",
//           firstAvailableOption.dataset.option3 || ""
//         ];
//       }

//       this.updateCardUi(card);
//     });
//   }

//   // ensureAtLeastOneChecked(changedCheckbox = null) {
//   //   const checked = this.cards.filter((card) => {
//   //     return card.querySelector("[data-upsell-product-checkbox]")?.checked;
//   //   });

//   //   if (checked.length) return;

//   //   if (changedCheckbox) {
//   //     changedCheckbox.checked = true;
//   //     return;
//   //   }

//   //   const firstCheckbox = this.cards[0]?.querySelector("[data-upsell-product-checkbox]");
//   //   if (firstCheckbox) firstCheckbox.checked = true;
//   // }

//   isCardSelected(card) {
//     return !!card.querySelector("[data-upsell-product-checkbox]")?.checked;
//   }

//   syncAllCardsUi() {
//     this.cards.forEach((card) => {
//       card.classList.toggle("is-selected", this.isCardSelected(card));
//       this.updateCardUi(card);
//     });
//   }

//   onChange(event) {
//   const checkbox = event.target.closest("[data-upsell-product-checkbox]");
//   if (!checkbox || !this.contains(checkbox)) return;

//   this.syncAllCardsUi();
// }

//   onClick(event) {
//     const pill = event.target.closest("[data-option-value]");
//     if (!pill || !this.contains(pill)) return;
//     if (pill.disabled) return;

//     const card = pill.closest("[data-upsell-card]");
//     if (!card || !this.isCardSelected(card)) return;

//     const group = pill.closest("[data-option-index]");
//     if (!group) return;

//     const idx = Number(group.dataset.optionIndex);
//     const value = pill.dataset.optionValue || "";
//     const state = card.__upsellState;

//     if (state.selected[idx] === value) return;

//     state.selected[idx] = value;
//     this.autofillMissingOptions(card);
//     this.refreshCardFromSelection(card);
//   }

//   autofillMissingOptions(card) {
//     const state = card.__upsellState;
//     const map = card.querySelector("[data-upsell-variant-map]");
//     if (!map) return;

//     const options = Array.from(map.options).filter((option) => {
//       if (!option.value) return false;
//       return this.matchesPartialSelection(option, state.selected);
//     });

//     const candidate = options.find((option) => option.dataset.available === "true") || options[0];
//     if (!candidate) return;

//     if (!state.selected[0] && candidate.dataset.option1) state.selected[0] = candidate.dataset.option1;
//     if (!state.selected[1] && candidate.dataset.option2) state.selected[1] = candidate.dataset.option2;
//     if (!state.selected[2] && candidate.dataset.option3) state.selected[2] = candidate.dataset.option3;
//   }

//   matchesPartialSelection(option, selected) {
//     return [1, 2, 3].every((n, index) => {
//       const selectedValue = selected[index];
//       if (!selectedValue) return true;
//       return (option.dataset[`option${n}`] || "") === selectedValue;
//     });
//   }

//   hasCompleteSelection(card) {
//     const groups = card.querySelectorAll("[data-option-index]");
//     const state = card.__upsellState;

//     return Array.from(groups).every((group) => {
//       const idx = Number(group.dataset.optionIndex);
//       return !!state.selected[idx];
//     });
//   }

//   refreshCardFromSelection(card) {
//     const state = card.__upsellState;
//     const map = card.querySelector("[data-upsell-variant-map]");
//     const variantIdInput = card.querySelector("[data-upsell-variant-id]");
//     const priceEl = card.querySelector("[data-upsell-price]");
//     const helpEl = card.querySelector("[data-upsell-help]");
//     const options = Array.from(map?.options || []).filter((option) => option.value);

//     const exact = options.find((option) => {
//       return [1, 2, 3].every((n, index) => {
//         const current = state.selected[index];
//         if (!current) return true;
//         return (option.dataset[`option${n}`] || "") === current;
//       });
//     });

//     if (exact && exact.value && exact.dataset.available === "true" && this.hasCompleteSelection(card)) {
//       if (variantIdInput) variantIdInput.value = exact.value;
//       if (map) map.value = exact.value;
//       if (priceEl) {
//         this.setPriceDisplay(
//           priceEl,
//           exact.dataset.price || "",
//           exact.dataset.comparePrice || ""
//         );
//       }
//       if (helpEl) helpEl.textContent = "";
//     } else {
//       if (variantIdInput) variantIdInput.value = "";
//       if (map) map.value = "";
//       if (priceEl) this.setPriceDisplay(priceEl, "Select options", "");
//       if (helpEl) {
//         helpEl.textContent = this.hasCompleteSelection(card)
//           ? "This combination is unavailable."
//           : "Please select all required options.";
//       }
//     }

//     this.updateCardUi(card);
//   }

//   updateCardUi(card) {
//     const state = card.__upsellState;
//     const groups = card.querySelectorAll("[data-option-index]");
//     const map = card.querySelector("[data-upsell-variant-map]");
//     const options = Array.from(map?.options || []).filter((option) => option.value);

//     groups.forEach((group) => {
//       const idx = Number(group.dataset.optionIndex);
//       const selectedLabel = group.querySelector("[data-selected-label]");
//       const pills = Array.from(group.querySelectorAll("[data-option-value]"));

//       if (selectedLabel) selectedLabel.textContent = state.selected[idx] || "";

//       pills.forEach((pill) => {
//         const value = pill.dataset.optionValue || "";
//         const active = state.selected[idx] === value;

//         pill.setAttribute("aria-pressed", active ? "true" : "false");
//         pill.classList.toggle("is-active", active);

//         const possible = options.some((option) => {
//           const probe = [...state.selected];
//           probe[idx] = value;
//           return this.matchesPartialSelection(option, probe) && option.dataset.available === "true";
//         });

//         pill.disabled = !possible;
//       });
//     });
//   }

//   getEligibleUpsellItems() {
//     return this.cards
//       .filter((card) => this.isCardSelected(card))
//       .map((card) => {
//         const variantId = String(card.querySelector("[data-upsell-variant-id]")?.value || "").trim();

//         if (!variantId) return null;

//         return {
//           id: Number(variantId),
//           quantity: 1
//         };
//       })
//       .filter(Boolean);
//   }

//   async onSubmit(event) {
//     const form = event.target;
//     if (form !== this.mainForm) return;
//     if (!form.matches("[data-upsell-aware='true']")) return;

//     const idInput = form.querySelector('input[name="id"]');
//     if (!idInput?.value) return;

//     const upsellItems = this.getEligibleUpsellItems();
//     if (!upsellItems.length) return;

//     event.preventDefault();
//     event.stopPropagation();

//     const quantityInput = form.querySelector('input[name="quantity"]');
//     const mainQty = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
//     const mainVariantId = Number(idInput.value);

//     const submitButtons = Array.from(form.querySelectorAll('[type="submit"]'));
//     submitButtons.forEach((button) => button.setAttribute("aria-busy", "true"));

//     document.documentElement.dispatchEvent(
//       new CustomEvent("theme:loading:start", { bubbles: true })
//     );

//     try {
//       await this.addBundleWithThemeEvents(
//         form,
//         { id: mainVariantId, quantity: mainQty },
//         upsellItems
//       );
//     } finally {
//       submitButtons.forEach((button) => button.removeAttribute("aria-busy"));
//       document.documentElement.dispatchEvent(
//         new CustomEvent("theme:loading:end", { bubbles: true })
//       );
//     }
//   }

//   async addBundleWithThemeEvents(mainForm, mainItem, upsellItems) {
//     const sectionsToBundle = new Set();

//     document.documentElement.dispatchEvent(
//       new CustomEvent("cart:prepare-bundled-sections", {
//         bubbles: true,
//         detail: { sections: sectionsToBundle }
//       })
//     );

//     const payload = {
//       items: [mainItem, ...upsellItems],
//       sections: Array.from(sectionsToBundle).join(",")
//     };

//     const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         "Accept": "application/json",
//         "X-Requested-With": "XMLHttpRequest"
//       },
//       body: JSON.stringify(payload)
//     });

//     const responseJson = await response.json();

//     if (!response.ok) {
//       mainForm.dispatchEvent(
//         new CustomEvent("cart:error", {
//           bubbles: true,
//           detail: { error: responseJson.message }
//         })
//       );

//       document.dispatchEvent(
//         new CustomEvent("toast:show", {
//           detail: {
//             message: responseJson.message,
//             tone: "error"
//           }
//         })
//       );

//       document.documentElement.dispatchEvent(
//         new CustomEvent("cart:refresh", { bubbles: true })
//       );

//       return;
//     }

//     if (
//       window.themeVariables?.settings?.cartType === "page" ||
//       window.themeVariables?.settings?.pageType === "cart"
//     ) {
//       window.location.href = `${Shopify.routes.root}cart`;
//       return;
//     }

//     const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
//     cartContent.sections = responseJson.sections;

//     const items = responseJson.hasOwnProperty("items") ? responseJson.items : [responseJson];

//     mainForm.dispatchEvent(
//       new CustomEvent("variant:add", {
//         bubbles: true,
//         detail: { items, cart: cartContent }
//       })
//     );

//     document.documentElement.dispatchEvent(
//       new CustomEvent("cart:change", {
//         bubbles: true,
//         detail: {
//           baseEvent: "variant:add",
//           cart: cartContent
//         }
//       })
//     );

//     if (window.themeVariables?.settings?.cartType === "message") {
//       document.dispatchEvent(
//         new CustomEvent("toast:show", {
//           detail: {
//             message: window.themeVariables.strings.addedToCart.replace("{{product_title}}", items?.[0]?.title || "")
//           }
//         })
//       );
//     }
//   }
// }

// if (!window.customElements.get("upsell-checkbox-bundle")) {
//   window.customElements.define("upsell-checkbox-bundle", UpsellCheckboxBundle);
// }
